/**
 * Local Bridge: manage users in the local database (list, add).
 * Uses backend Local Bridge API (PostgreSQL).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, Form, Input, Button, Table, message, Spin, Alert, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import { listUsers, createUser, updateUser, type LocalUser } from '../services/localBridgeSessionApi';

export const LocalBridgeUsersPage: React.FC = () => {
  const [users, setUsers] = useState<LocalUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ name: string; email?: string; password?: string }>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<LocalUser | null>(null);
  const [editForm] = Form.useForm<{ name: string; email?: string }>();
  const [submittingEdit, setSubmittingEdit] = useState(false);

  const enabled = isLocalBridgeApiEnabled();

  const loadUsers = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const list = await listUsers();
      setUsers(list);
    } catch {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const user = await createUser(values.name, values.email, values.password);
      if (user) {
        message.success('User created');
        form.resetFields();
        loadUsers();
      } else {
        message.error('Failed to create user');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please enter user name');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (user: LocalUser) => {
    setEditingUser(user);
    editForm.setFieldsValue({ name: user.name, email: user.email ?? '' });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
    editForm.resetFields();
  };

  const handleEditSubmit = async () => {
    if (!editingUser) return;
    try {
      const values = await editForm.validateFields();
      setSubmittingEdit(true);
      const updated = await updateUser(editingUser.id, {
        name: values.name,
        email: values.email?.trim() || undefined,
      });
      if (updated) {
        message.success('User updated');
        closeEditModal();
        loadUsers();
      } else {
        message.error('Failed to update user');
      }
    } catch (e) {
      if (e && typeof e === 'object' && 'errorFields' in e) return;
      message.error('Please enter user name');
    } finally {
      setSubmittingEdit(false);
    }
  };

  if (!enabled) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="info"
          message="Local Bridge API not configured"
          description="Local Bridge API is not available. Ensure the backend is running."
        />
      </div>
    );
  }

  const columns: ColumnsType<LocalUser> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email', render: (v: string | undefined) => v ?? '—' },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => id.slice(0, 8) + '…',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />}>Local Bridge</Button>
        </Link>
      </div>
      <h1 style={{ marginBottom: 24 }}>Users</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Users are shared across the app. Create users here for bridge sessions; they can log in with email and password.
      </p>

      <Card title="Add user" style={{ marginBottom: 24 }}>
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="User name" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" placeholder="Optional (used as username for login)" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Form.Item name="password" label="Password">
            <Input.Password placeholder="Optional (auto-generated if blank)" style={{ maxWidth: 320 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Create user
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Users">
        {loading ? (
          <Spin size="large" />
        ) : users.length === 0 ? (
          <p style={{ color: '#6b7280' }}>No users yet. Add a user above.</p>
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={users}
            pagination={false}
            size="middle"
          />
        )}
      </Card>

      <Modal
        title="Edit user"
        open={editModalOpen}
        onCancel={closeEditModal}
        onOk={handleEditSubmit}
        confirmLoading={submittingEdit}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="User name" />
          </Form.Item>
          <Form.Item name="email" label="Email">
            <Input type="email" placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LocalBridgeUsersPage;
